import { useState } from "react";

interface ErrorInfo {
  klass: string;
  message: string;
  backtrace: string[];
  url: string;
  timestamp: string;
}

interface ErrorProps {
  error?: string | ErrorInfo;
}

function buildLlmsText(error: ErrorInfo): string {
  return `# Error Report

## Summary
- **Class**: ${error.klass}
- **Message**: ${error.message}
- **URL**: ${error.url}
- **Timestamp**: ${error.timestamp}

## Backtrace
${error.backtrace.map((line) => `  ${line}`).join("\n")}
`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="fixed bottom-4 right-4 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

function DevError({ info }: { info: ErrorInfo }) {
  const llmsText = buildLlmsText(info);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-xl border border-red-200 shadow-sm max-w-2xl w-full mx-4 p-6">
        <div className="flex items-start justify-between">
          <h1 className="text-lg font-bold text-red-700">{info.klass}</h1>
          <button
            onClick={() => navigator.clipboard.writeText(llmsText)}
            className="shrink-0 ml-4 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          >
            llms.txt コピー
          </button>
        </div>

        <pre className="mt-3 text-xs text-slate-700 bg-red-50 rounded-lg p-3 overflow-auto whitespace-pre-wrap font-mono leading-relaxed">
          {info.message}
        </pre>

        {info.url && (
          <div className="mt-3 text-xs text-slate-500">
            <span className="font-medium">URL:</span> {info.url}
          </div>
        )}
        {info.timestamp && (
          <div className="mt-1 text-xs text-slate-500">
            <span className="font-medium">Time:</span> {info.timestamp}
          </div>
        )}

        {info.backtrace.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
              Backtrace ({info.backtrace.length} frames)
            </summary>
            <pre className="mt-2 text-[11px] text-slate-500 bg-slate-50 rounded-lg p-3 overflow-auto whitespace-pre-wrap font-mono leading-relaxed max-h-60">
              {info.backtrace.join("\n")}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default function Error({ error }: ErrorProps) {
  if (!error) return null;

  const info = typeof error === "string"
    ? { klass: "Error", message: error, backtrace: [] as string[], url: "", timestamp: "" }
    : error;

  const llmsText = buildLlmsText(info);

  if (import.meta.env.DEV) {
    return <DevError info={info} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center px-6">
        <p className="text-slate-400 text-5xl leading-none">500</p>
        <p className="mt-4 text-sm text-slate-500">ページを表示できませんでした</p>
        <p className="mt-2 text-xs text-slate-400">時間をおいて再度お試しください</p>
      </div>
      <CopyButton text={llmsText} />
    </div>
  );
}
